// FahrzeugePage - Fahrzeugverwaltung mit Flip-Card Modal
import React, { useState, useEffect } from 'react';
import { 
  Truck, AlertCircle, CheckCircle2, Clock, Wrench,
  Edit, Calendar, Package, Plus, X, History, ArrowLeftRight,
  ChevronRight, Shield, AlertTriangle, User, MessageSquare,
  Send, RotateCcw, Trash2, Sun, Moon, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { fahrzeugApi, dienstApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { cn, formatDate } from '../lib/utils';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

// =============================================
// Konstanten
// =============================================

const FAHRZEUG_BADGE_FARBEN = {
  'LHF': { bg: 'bg-red-500', text: 'text-white' },
  'DLK': { bg: 'bg-orange-500', text: 'text-white' },
  'RTW': { bg: 'bg-blue-500', text: 'text-white' },
  'NEF': { bg: 'bg-yellow-500', text: 'text-black' },
  'ELW': { bg: 'bg-slate-500', text: 'text-white' },
  'TLF': { bg: 'bg-red-600', text: 'text-white' },
  'HLF': { bg: 'bg-red-500', text: 'text-white' },
  'GW': { bg: 'bg-green-500', text: 'text-white' },
  'default': { bg: 'bg-gray-500', text: 'text-white' }
};

const STATUS_CONFIG = {
  'EINSATZBEREIT': { color: 'text-emerald-600', bg: 'bg-emerald-500', label: 'Einsatzbereit' },
  'IM_EINSATZ': { color: 'text-red-600', bg: 'bg-red-500', label: 'Im Einsatz' },
  'WERKSTATT': { color: 'text-orange-600', bg: 'bg-orange-500', label: 'Werkstatt' },
  'AUSSERDIENST': { color: 'text-gray-600', bg: 'bg-gray-500', label: 'Außer Dienst' },
  'default': { color: 'text-emerald-600', bg: 'bg-emerald-500', label: 'Einsatzbereit' }
};

import { FAHRZEUG_BILDER as FAHRZEUG_BILDER_CENTRAL, useFahrzeugBilder } from '../lib/fahrzeugBilder';

const FAHRZEUG_BILDER = FAHRZEUG_BILDER_CENTRAL;

// Fahrzeug-Typen pro Haupttyp
const FAHRZEUG_TYPEN_DETAIL = {
  'LHF': ['City', 'CAFS', 'DLS', 'Standard'],
  'HLF': ['10', '20', 'Standard'],
  'DLK': ['23-12', '18-12', 'Standard'],
  'RTW': ['Standard', 'KTW'],
  'NEF': ['Standard'],
  'ELW': ['1', '2', 'Standard'],
  'TLF': ['16/24', '24/50', 'Standard'],
  'GW': ['L', 'AS', 'Hygiene', 'Standard'],
};

const extractTypCode = (code) => {
  if (!code) return 'LHF';
  const match = code.match(/^([A-Z]+)/);
  return match ? match[1] : 'LHF';
};

// Liefert das anzuzeigende Variant-Label.
// Bei RTWs hat `subtyp` (B/C) Vorrang vor `typ_detail` (z. B. "Standard").
const getVariantLabel = (fahrzeug) => {
  if (!fahrzeug) return '';
  const code = extractTypCode(fahrzeug.fahrzeugtyp_code);
  if (code === 'RTW' && fahrzeug.subtyp) {
    return `RTW-${fahrzeug.subtyp}`;
  }
  return fahrzeug.typ_detail || '';
};

// =============================================
// Fahrzeug-Karte
// =============================================

function FahrzeugKarte({ fahrzeug, onClick }) {
  const typCode = extractTypCode(fahrzeug.fahrzeugtyp_code || fahrzeug.typ);
  const badgeFarbe = FAHRZEUG_BADGE_FARBEN[typCode] || FAHRZEUG_BADGE_FARBEN.default;
  const statusInfo = STATUS_CONFIG[fahrzeug.status] || STATUS_CONFIG.default;
  const bildPfad = FAHRZEUG_BILDER[typCode] || '/images/fahrzeuge/lhf.png';
  
  // Check for TÜV warnings
  const hasExpiredTuev = fahrzeug.geraete?.some(g => 
    g.ablaufdatum && new Date(g.ablaufdatum) < new Date()
  );
  
  return (
    <Card 
      className={cn(
        "relative overflow-hidden transition-all duration-300 cursor-pointer group",
        "hover:shadow-xl hover:-translate-y-1 hover:border-primary/50",
        "bg-gradient-to-b from-white to-slate-50/50"
      )}
      onClick={() => onClick(fahrzeug)}
    >
      {/* Status-Indikator + Warnungen */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        <div className={cn('w-3 h-3 rounded-full ring-2 ring-white shadow-sm', statusInfo.bg)} />
        {hasExpiredTuev && (
          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
            <AlertTriangle className="w-3 h-3 mr-0.5" />TÜV
          </Badge>
        )}
      </div>
      
      {/* Fahrzeug-Bild */}
      <div className="relative h-44 flex items-center justify-center p-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100/50 to-transparent" />
        <img 
          src={bildPfad}
          alt={fahrzeug.funkrufname}
          className="relative max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-110"
          onError={(e) => { e.target.src = '/images/fahrzeuge/lhf.png'; }}
        />
      </div>
      
      {/* Info-Bereich */}
      <CardContent className="p-4 pt-3 border-t">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-slate-900 truncate leading-tight">
              {fahrzeug.funkrufname}
            </h3>
            <p className="text-sm text-muted-foreground font-mono">
              {fahrzeug.kennzeichen || 'Kein Kennzeichen'}
            </p>
            {/* Typ-Detail anzeigen (RTW: subtyp B/C, sonst typ_detail) */}
            {(() => {
              const variantLabel = getVariantLabel(fahrzeug);
              return variantLabel ? (
                <Badge variant="outline" className="mt-1 text-xs">
                  {variantLabel}
                </Badge>
              ) : null;
            })()}
          </div>
          <Badge className={cn('shrink-0 font-bold', badgeFarbe.bg, badgeFarbe.text)}>
            {typCode}
          </Badge>
        </div>
        
        {/* Status + Chevron */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-dashed">
          <div className="flex items-center gap-2">
            <CheckCircle2 className={cn('w-4 h-4', statusInfo.color)} />
            <span className={cn('text-sm font-medium', statusInfo.color)}>{statusInfo.label}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================
// Flip-Card Modal mit Details & Takel-Verlauf
// =============================================

function FahrzeugDetailsModal({ fahrzeug, open, onOpenChange, onUpdate }) {
  const { user } = useAuthStore();
  const [isFlipped, setIsFlipped] = useState(false);
  const [takelHistorie, setTakelHistorie] = useState([]);
  const [kommentare, setKommentare] = useState([]);
  const [isLoadingHistorie, setIsLoadingHistorie] = useState(false);
  const [isLoadingKommentare, setIsLoadingKommentare] = useState(false);
  const [neuerKommentar, setNeuerKommentar] = useState('');
  const [isSendingKommentar, setIsSendingKommentar] = useState(false);
  
  // Sub-Dialogs
  const [takelDialogOpen, setTakelDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [geraetDialogOpen, setGeraetDialogOpen] = useState(false);
  const [selectedGeraet, setSelectedGeraet] = useState(null); // Für Bearbeiten
  
  useEffect(() => {
    if (fahrzeug && open) {
      setIsFlipped(false);
      loadTakelHistorie();
      loadKommentare();
    }
  }, [fahrzeug, open]);
  
  const loadTakelHistorie = async () => {
    if (!fahrzeug) return;
    setIsLoadingHistorie(true);
    try {
      const res = await dienstApi.getTakelHistorie(fahrzeug.id, { limit: 20 });
      setTakelHistorie(res.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Takel-Historie:', error);
    } finally {
      setIsLoadingHistorie(false);
    }
  };
  
  const loadKommentare = async () => {
    if (!fahrzeug) return;
    setIsLoadingKommentare(true);
    try {
      const res = await dienstApi.getKommentare?.(fahrzeug.id, { limit: 50 }) || 
                   await fahrzeugApi.getKommentare(fahrzeug.id, { limit: 50 });
      setKommentare(res.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Kommentare:', error);
    } finally {
      setIsLoadingKommentare(false);
    }
  };
  
  const handleSendKommentar = async () => {
    if (!neuerKommentar.trim()) return;
    setIsSendingKommentar(true);
    try {
      await fahrzeugApi.createKommentar(fahrzeug.id, { 
        fahrzeug_id: fahrzeug.id,
        text: neuerKommentar.trim() 
      });
      setNeuerKommentar('');
      loadKommentare();
      toast.success('Kommentar hinzugefügt');
    } catch (error) {
      console.error('Fehler:', error);
      toast.error('Fehler beim Senden');
    } finally {
      setIsSendingKommentar(false);
    }
  };
  
  if (!fahrzeug) return null;
  
  const typCode = extractTypCode(fahrzeug.fahrzeugtyp_code || fahrzeug.typ);
  const badgeFarbe = FAHRZEUG_BADGE_FARBEN[typCode] || FAHRZEUG_BADGE_FARBEN.default;
  const statusInfo = STATUS_CONFIG[fahrzeug.status] || STATUS_CONFIG.default;
  const bildPfad = FAHRZEUG_BILDER[typCode] || '/images/fahrzeuge/lhf.png';
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden max-h-[90vh] flex flex-col">
          {/* Accessibility: VisuallyHidden Title */}
          <DialogHeader className="sr-only">
            <DialogTitle>{fahrzeug.funkrufname}</DialogTitle>
          </DialogHeader>
          
          {/* Flip Container */}
          <div className="relative flex-1 overflow-y-auto" style={{ perspective: '1500px' }}>
            <div 
              className={cn(
                "transition-transform duration-500",
                isFlipped && "[transform:rotateY(180deg)]"
              )}
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* === VORDERSEITE === */}
              <div 
                className={cn(
                  "p-6",
                  isFlipped && "invisible"
                )}
                style={{ backfaceVisibility: 'hidden' }}
              >
                {/* Header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-20 h-20 rounded-xl bg-slate-100 flex items-center justify-center p-2 shrink-0">
                    <img src={bildPfad} alt={fahrzeug.funkrufname} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-2xl font-bold">{fahrzeug.funkrufname}</h2>
                      <Badge className={cn('font-bold', badgeFarbe.bg, badgeFarbe.text)}>{typCode}</Badge>
                      {(() => {
                        const variantLabel = getVariantLabel(fahrzeug);
                        return variantLabel ? (
                          <Badge variant="outline">{variantLabel}</Badge>
                        ) : null;
                      })()}
                    </div>
                    <p className="text-muted-foreground font-mono text-lg">{fahrzeug.kennzeichen || '-'}</p>
                  </div>
                </div>
                
                {/* Schnellaktionen */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Button variant="default" className="h-11 gap-2" onClick={() => setTakelDialogOpen(true)}>
                    <ArrowLeftRight className="w-4 h-4" />
                    Takeln
                  </Button>
                  {['DEVELOPER', 'WACHLEITUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'].includes(user?.rolle) ? (
                    <Button variant="outline" className="h-11 gap-2" onClick={() => setEditDialogOpen(true)}>
                      <Edit className="w-4 h-4" />
                      Bearbeiten
                    </Button>
                  ) : (
                    <Button variant="outline" className="h-11 gap-2 opacity-50" disabled title="Nur für Wachleitung">
                      <Edit className="w-4 h-4" />
                      Bearbeiten
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Button variant="secondary" className="h-11 gap-2" onClick={() => setIsFlipped(true)}>
                    <History className="w-4 h-4" />
                    Verlauf
                  </Button>
                  {['DEVELOPER', 'WACHLEITUNG', 'ADMIN'].includes(user?.rolle) && (
                    <Button 
                      variant="destructive" 
                      className="h-11 gap-2" 
                      onClick={async () => {
                        if (!confirm(`Fahrzeug "${fahrzeug.funkrufname}" wirklich löschen?`)) return;
                        try {
                          await dienstApi.deleteFahrzeug(fahrzeug.id);
                          toast.success('Fahrzeug gelöscht');
                          onOpenChange(false);
                          if (onRefresh) onRefresh();
                        } catch (err) {
                          toast.error('Fehler beim Löschen');
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Löschen
                    </Button>
                  )}
                </div>
                
                <Separator className="my-4" />
                
                {/* Fahrzeug-Daten Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Fahrzeugtyp</p>
                    <p className="font-semibold">{fahrzeug.fahrzeugtyp_code || typCode}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Status</p>
                    <div className="flex items-center gap-2">
                      <div className={cn('w-2 h-2 rounded-full', statusInfo.bg)} />
                      <span className="font-semibold">{statusInfo.label}</span>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Kennzeichen</p>
                    <p className="font-semibold font-mono">{fahrzeug.kennzeichen || '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Typ-Variante</p>
                    <p className="font-semibold">{getVariantLabel(fahrzeug) || 'Standard'}</p>
                  </div>
                </div>
                
                {/* Sollstärke */}
                <div className="mb-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />Sollstärke (Besatzung)
                  </h3>
                  <div className="flex items-stretch border rounded-lg overflow-hidden bg-card">
                    <div className="flex-1 flex items-center gap-3 px-4 py-3">
                      <Sun className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Tag</p>
                        <p className="text-xl font-semibold tabular-nums leading-tight">
                          {fahrzeug.staerke_tag ?? 2}
                          <span className="text-xs font-normal text-muted-foreground ml-1.5">Pers.</span>
                        </p>
                      </div>
                    </div>
                    <div className="w-px bg-border" />
                    <div className="flex-1 flex items-center gap-3 px-4 py-3">
                      <Moon className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Nacht</p>
                        <p className="text-xl font-semibold tabular-nums leading-tight">
                          {fahrzeug.staerke_nacht ?? 2}
                          <span className="text-xs font-normal text-muted-foreground ml-1.5">Pers.</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Geräte & TÜV */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <Shield className="w-4 h-4" />Geräte & TÜV
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedGeraet(null); setGeraetDialogOpen(true); }}>
                      <Plus className="w-4 h-4 mr-1" />Hinzufügen
                    </Button>
                  </div>
                  {(!fahrzeug.geraete || fahrzeug.geraete.length === 0) ? (
                    <div className="text-center py-4 text-muted-foreground bg-muted/30 rounded-lg text-sm">
                      Keine Geräte eingetragen
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {fahrzeug.geraete.map((geraet, idx) => {
                        const isExpired = geraet.ablaufdatum && new Date(geraet.ablaufdatum) < new Date();
                        const isSoon = geraet.ablaufdatum && !isExpired && 
                          (new Date(geraet.ablaufdatum) - new Date()) / (1000 * 60 * 60 * 24) <= 30;
                        return (
                          <div 
                            key={geraet.id || idx} 
                            className={cn(
                              'flex items-center justify-between p-2 rounded-lg border text-sm cursor-pointer transition-colors',
                              isExpired ? 'bg-red-50 border-red-200 hover:bg-red-100' : 
                              isSoon ? 'bg-amber-50 border-amber-200 hover:bg-amber-100' :
                              'bg-white hover:bg-slate-50'
                            )}
                            onClick={() => { setSelectedGeraet(geraet); setGeraetDialogOpen(true); }}
                          >
                            <div className="flex items-center gap-2">
                              {isExpired && <AlertTriangle className="w-4 h-4 text-red-500" />}
                              {isSoon && !isExpired && <Clock className="w-4 h-4 text-amber-500" />}
                              <span className="font-medium">{geraet.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {geraet.ablaufdatum && (
                                <Badge 
                                  variant={isExpired ? 'destructive' : isSoon ? 'secondary' : 'outline'} 
                                  className={cn('text-xs', isSoon && !isExpired && 'bg-amber-100 text-amber-800')}
                                >
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDate(geraet.ablaufdatum, { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                </Badge>
                              )}
                              <Edit className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <Separator className="my-4" />
                
                {/* Kommentare */}
                <div>
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4" />Kommentare
                  </h3>
                  
                  {/* Kommentar-Eingabe */}
                  <div className="flex gap-2 mb-3">
                    <Input 
                      placeholder="Kommentar schreiben..."
                      value={neuerKommentar}
                      onChange={(e) => setNeuerKommentar(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendKommentar()}
                      className="flex-1"
                    />
                    <Button size="icon" onClick={handleSendKommentar} disabled={isSendingKommentar || !neuerKommentar.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Kommentar-Liste */}
                  <ScrollArea className="h-32">
                    {isLoadingKommentare ? (
                      <Skeleton className="h-20" />
                    ) : kommentare.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        Noch keine Kommentare
                      </div>
                    ) : (
                      <div className="space-y-2 pr-2">
                        {kommentare.map((k, idx) => (
                          <div key={idx} className="p-2 rounded-lg bg-muted/30 text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <Avatar className="w-5 h-5">
                                <AvatarFallback className="text-[10px]">
                                  {k.user_name?.split(' ').map(n => n[0]).join('') || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-xs">{k.user_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(k.erstellt_am, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-slate-700">{k.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
              
              {/* === RÜCKSEITE (Takel-Verlauf) === */}
              <div 
                className={cn(
                  "absolute inset-0 p-6 bg-white",
                  !isFlipped && "invisible"
                )}
                style={{ 
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)'
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => setIsFlipped(false)}>
                      <RotateCcw className="w-5 h-5" />
                    </Button>
                    <div>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <History className="w-5 h-5" />
                        Takel-Verlauf
                      </h2>
                      <p className="text-sm text-muted-foreground">{fahrzeug.funkrufname}</p>
                    </div>
                  </div>
                  <Badge className={cn('font-bold', badgeFarbe.bg, badgeFarbe.text)}>{typCode}</Badge>
                </div>
                
                <Separator className="mb-4" />
                
                {/* Takel-Historie */}
                <ScrollArea className="h-[400px]">
                  {isLoadingHistorie ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
                    </div>
                  ) : takelHistorie.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">Noch keine Takel-Vorgänge</p>
                      <p className="text-sm">Hier erscheint der Verlauf aller Fahrzeugwechsel</p>
                    </div>
                  ) : (
                    <div className="space-y-3 pr-4">
                      {takelHistorie.map((log, idx) => (
                        <div 
                          key={idx}
                          className={cn(
                            'p-4 rounded-lg border',
                            log.ist_storniert ? 'opacity-50 bg-slate-50' : 'bg-white'
                          )}
                        >
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-bold text-red-600">{log.von_kennzeichen}</span>
                              <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
                              <span className="font-mono font-bold text-emerald-600">{log.zu_kennzeichen}</span>
                            </div>
                            {log.ist_storniert && <Badge variant="destructive">Storniert</Badge>}
                          </div>
                          
                          {/* Typ-Wechsel anzeigen */}
                          {(log.von_typ_detail || log.zu_typ_detail) && (
                            <div className="flex items-center gap-2 text-sm mb-2">
                              <span className="text-muted-foreground">Typ:</span>
                              <Badge variant="outline">{log.von_typ_detail || 'Standard'}</Badge>
                              <span>→</span>
                              <Badge variant="secondary">{log.zu_typ_detail || 'Standard'}</Badge>
                            </div>
                          )}
                          
                          <p className="text-sm text-slate-600 mb-2">{log.bemerkung}</p>
                          {log.zielort && (
                            <p className="text-xs text-muted-foreground mb-2">Zielort: {log.zielort}</p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <User className="w-3 h-3" />
                            <span>{log.user_name}</span>
                            <span>•</span>
                            <span>{formatDate(log.erstellt_am, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Sub-Dialogs */}
      <TakelnDialog 
        fahrzeug={fahrzeug}
        open={takelDialogOpen}
        onOpenChange={setTakelDialogOpen}
        onSuccess={() => { loadTakelHistorie(); if (onUpdate) onUpdate(); }}
      />
      <BearbeitenDialog
        fahrzeug={fahrzeug}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={() => { if (onUpdate) onUpdate(); }}
      />
      <GeraetDialog
        fahrzeug={fahrzeug}
        geraet={selectedGeraet}
        open={geraetDialogOpen}
        onOpenChange={(open) => { setGeraetDialogOpen(open); if (!open) setSelectedGeraet(null); }}
        onSuccess={() => { if (onUpdate) onUpdate(); }}
      />
    </>
  );
}

// =============================================
// Takeln Dialog (mit Typ-Wechsel)
// =============================================

function TakelnDialog({ fahrzeug, open, onOpenChange, onSuccess }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    zu_kennzeichen: '',
    zu_typ_detail: '',
    bemerkung: '',
    zielort: ''
  });
  
  const typCode = extractTypCode(fahrzeug?.fahrzeugtyp_code || fahrzeug?.typ);
  const verfuegbareTypen = FAHRZEUG_TYPEN_DETAIL[typCode] || ['Standard'];
  
  useEffect(() => {
    if (open && fahrzeug) {
      setFormData({ 
        zu_kennzeichen: '', 
        zu_typ_detail: fahrzeug.typ_detail || '',
        bemerkung: '', 
        zielort: '' 
      });
    }
  }, [open, fahrzeug]);
  
  const handleSubmit = async () => {
    if (!formData.zu_kennzeichen || !formData.bemerkung) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setIsLoading(true);
    try {
      await dienstApi.takel(fahrzeug.id, {
        von_kennzeichen: fahrzeug.kennzeichen,
        zu_kennzeichen: formData.zu_kennzeichen,
        von_typ_detail: fahrzeug.typ_detail || null,
        zu_typ_detail: formData.zu_typ_detail || null,
        bemerkung: formData.bemerkung,
        zielort: formData.zielort || null
      });
      
      toast.success('Fahrzeug erfolgreich getakelt');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Takeln Fehler:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Takeln';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!fahrzeug) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-primary" />
            Fahrzeug takeln
          </DialogTitle>
          <DialogDescription>{fahrzeug.funkrufname}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Aktuelles Kennzeichen</Label>
              <Input value={fahrzeug.kennzeichen || ''} disabled className="font-mono bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>Neues Kennzeichen *</Label>
              <Input 
                placeholder="B-1234"
                value={formData.zu_kennzeichen}
                onChange={(e) => setFormData(prev => ({ ...prev, zu_kennzeichen: e.target.value }))}
                className="font-mono"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Aktueller Typ</Label>
              <Input value={fahrzeug.typ_detail || 'Standard'} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>Neuer Typ</Label>
              <Select value={formData.zu_typ_detail} onValueChange={(v) => setFormData(prev => ({ ...prev, zu_typ_detail: v }))}>
                <SelectTrigger><SelectValue placeholder="Typ wählen..." /></SelectTrigger>
                <SelectContent>
                  {verfuegbareTypen.map(typ => (
                    <SelectItem key={typ} value={typ}>{typ}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Grund / Bemerkung *</Label>
            <Textarea 
              placeholder="z.B. Ersatzfahrzeug wegen Werkstattaufenthalt"
              value={formData.bemerkung}
              onChange={(e) => setFormData(prev => ({ ...prev, bemerkung: e.target.value }))}
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Zielort (optional)</Label>
            <Input 
              placeholder="z.B. Werkstatt Mitte"
              value={formData.zielort}
              onChange={(e) => setFormData(prev => ({ ...prev, zielort: e.target.value }))}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Wird getakelt...' : 'Takeln bestätigen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// Bearbeiten Dialog (mit Name und Typ)
// =============================================

function BearbeitenDialog({ fahrzeug, open, onOpenChange, onSuccess }) {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    funkrufname: '',
    kennzeichen: '',
    typ_detail: '',
    status: 'EINSATZBEREIT',
    dienstzeit_modell: '24H',
    individuelle_start: '09:00',
    individuelle_ende: '21:00',
    individuelle_wochentage: [0,1,2,3,4,5,6],
    staerke_tag: 2,
    staerke_nacht: 2,
    kann_besprungen_werden: false,
    ist_springer: false,
    tragen_typ: '',
    subtyp: '',
    use_custom_funktionen: false,
    custom_funktionen: [],
    magnetboard_farbe: '',
  });
  
  const typCode = extractTypCode(fahrzeug?.fahrzeugtyp_code || fahrzeug?.typ);
  const verfuegbareTypen = FAHRZEUG_TYPEN_DETAIL[typCode] || ['Standard'];
  const canEditSollstaerke = ['DEVELOPER', 'WACHLEITUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'].includes(user?.rolle);
  const canEditAll = ['DEVELOPER', 'WACHLEITUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'].includes(user?.rolle);
  const isRTW = typCode === 'RTW';
  
  useEffect(() => {
    if (fahrzeug && open) {
      setFormData({
        funkrufname: fahrzeug.funkrufname || '',
        kennzeichen: fahrzeug.kennzeichen || '',
        typ_detail: fahrzeug.typ_detail || '',
        status: fahrzeug.status || 'EINSATZBEREIT',
        dienstzeit_modell: fahrzeug.dienstzeit_modell || fahrzeug.dienstzeit_cfg?.typ || '24H',
        individuelle_start: fahrzeug.individuelle_dienstzeit?.start_zeit || '09:00',
        individuelle_ende: fahrzeug.individuelle_dienstzeit?.end_zeit || '21:00',
        individuelle_wochentage: fahrzeug.individuelle_dienstzeit?.wochentage || [0,1,2,3,4,5,6],
        staerke_tag: fahrzeug.staerke_tag ?? 2,
        staerke_nacht: fahrzeug.staerke_nacht ?? 2,
        kann_besprungen_werden: fahrzeug.kann_besprungen_werden || false,
        ist_springer: fahrzeug.ist_springer || false,
        tragen_typ: fahrzeug.tragen_typ || '',
        subtyp: fahrzeug.subtyp || '',
        // custom_funktionen: nur aktiv, wenn explizit pro Fahrzeug gesetzt
        use_custom_funktionen: Array.isArray(fahrzeug.custom_funktionen) && fahrzeug.custom_funktionen.length > 0,
        custom_funktionen: Array.isArray(fahrzeug.custom_funktionen) && fahrzeug.custom_funktionen.length > 0
          ? fahrzeug.custom_funktionen.map(f => ({
              kuerzel: f.kuerzel || '',
              name: f.name || '',
              ist_pflicht: f.ist_pflicht !== false,
            }))
          : (fahrzeug.funktionen || []).map(f => ({
              kuerzel: f.kuerzel || '',
              name: f.name || '',
              ist_pflicht: f.ist_pflicht !== false,
            })),
        magnetboard_farbe: fahrzeug.magnetboard_farbe || '',
      });
    }
  }, [fahrzeug, open]);
  
  const handleSubmit = async () => {
    if (!formData.funkrufname) {
      toast.error('Funkrufname ist erforderlich');
      return;
    }
    
    setIsLoading(true);
    try {
      const updateData = {
        funkrufname: formData.funkrufname,
        kennzeichen: formData.kennzeichen,
        typ_detail: formData.typ_detail || null,
        status: formData.status,
        dienstzeit_modell: formData.dienstzeit_modell
      };
      
      // Individuelle Dienstzeit für SONDER-Modell
      if (formData.dienstzeit_modell === 'SONDER') {
        updateData.individuelle_dienstzeit = {
          start_zeit: formData.individuelle_start,
          end_zeit: formData.individuelle_ende,
          wochentage: formData.individuelle_wochentage
        };
      }
      
      // Springer-Optionen
      if (canEditAll) {
        updateData.kann_besprungen_werden = formData.kann_besprungen_werden;
        updateData.ist_springer = formData.ist_springer;
        if (isRTW && formData.tragen_typ) {
          updateData.tragen_typ = formData.tragen_typ;
        }
      }
      
      // Sollstärke nur mit Berechtigung
      if (canEditSollstaerke) {
        const tagValue = parseInt(formData.staerke_tag);
        const nachtValue = parseInt(formData.staerke_nacht);
        updateData.staerke_tag = isNaN(tagValue) ? 2 : tagValue;
        updateData.staerke_nacht = isNaN(nachtValue) ? 2 : nachtValue;
      }

      // Custom-Funktionen: aktivieren oder zurücksetzen
      if (canEditAll) {
        if (formData.use_custom_funktionen) {
          // Validierung: Kürzel + Name sind Pflicht
          const cleaned = (formData.custom_funktionen || [])
            .map(f => ({
              kuerzel: (f.kuerzel || '').trim(),
              name: (f.name || '').trim(),
              ist_pflicht: f.ist_pflicht !== false,
            }))
            .filter(f => f.kuerzel.length > 0 && f.name.length > 0);
          if (cleaned.length === 0) {
            toast.error('Mindestens eine Funktion mit Kürzel und Name angeben');
            setIsLoading(false);
            return;
          }
          updateData.custom_funktionen = cleaned;
        } else {
          // Zurücksetzen auf Standard-Funktionen vom Typ
          updateData.custom_funktionen = [];
        }
        // Magnetboard-Farbe (leer = Default per Typ)
        updateData.magnetboard_farbe = (formData.magnetboard_farbe || '').trim() || null;
      }
      
      await dienstApi.updateFahrzeug(fahrzeug.id, updateData);
      toast.success('Fahrzeug aktualisiert');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleWochentag = (tag) => {
    setFormData(prev => {
      const wt = prev.individuelle_wochentage || [];
      if (wt.includes(tag)) {
        return { ...prev, individuelle_wochentage: wt.filter(t => t !== tag) };
      } else {
        return { ...prev, individuelle_wochentage: [...wt, tag].sort() };
      }
    });
  };
  
  const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  
  if (!fahrzeug) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />Fahrzeug bearbeiten
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Funkrufname *</Label>
            <Input 
              value={formData.funkrufname}
              onChange={(e) => setFormData(prev => ({ ...prev, funkrufname: e.target.value }))}
              disabled={!canEditAll}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Kennzeichen</Label>
            <Input 
              value={formData.kennzeichen}
              onChange={(e) => setFormData(prev => ({ ...prev, kennzeichen: e.target.value }))}
              className="font-mono"
              disabled={!canEditAll}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Typ-Variante</Label>
              <Select 
                value={formData.typ_detail} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, typ_detail: v }))}
                disabled={!canEditAll}
              >
                <SelectTrigger><SelectValue placeholder="Typ wählen..." /></SelectTrigger>
                <SelectContent>
                  {verfuegbareTypen.map(typ => (
                    <SelectItem key={typ} value={typ}>{typ}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EINSATZBEREIT">Einsatzbereit</SelectItem>
                  <SelectItem value="IM_EINSATZ">Im Einsatz</SelectItem>
                  <SelectItem value="WERKSTATT">Werkstatt</SelectItem>
                  <SelectItem value="AUSSERDIENST">Außer Dienst</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* RTW Tragen-Typ & Subtyp */}
          {isRTW && canEditAll && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tragen-Typ</Label>
                <Select value={formData.tragen_typ || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, tragen_typ: v }))}>
                  <SelectTrigger><SelectValue placeholder="Trage wählen..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STOLLENWERK">Stollenwerk</SelectItem>
                    <SelectItem value="STRYKER">Stryker Power-PRO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>RTW-Variante</Label>
                <Select value={formData.subtyp || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, subtyp: v }))}>
                  <SelectTrigger><SelectValue placeholder="B oder C..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B">RTW-B</SelectItem>
                    <SelectItem value="C">RTW-C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <Separator />
          
          {/* Dienstzeit */}
          <div className="space-y-2">
            <Label>Dienstzeit-Modell</Label>
            <Select 
              value={formData.dienstzeit_modell} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, dienstzeit_modell: v }))}
              disabled={!canEditAll}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24H">24-Stunden (07:00 - 07:00)</SelectItem>
                <SelectItem value="TAG">Nur Tagdienst (07:00 - 19:00)</SelectItem>
                <SelectItem value="NACHT">Nur Nachtdienst (19:00 - 07:00)</SelectItem>
                <SelectItem value="SONDER">Individuell</SelectItem>
                <SelectItem value="SPRINGER">Springer (keine feste Zeit)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Individuelle Dienstzeit */}
          {formData.dienstzeit_modell === 'SONDER' && (
            <div className="space-y-3 p-3 rounded-lg bg-muted/50">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Start</Label>
                  <Input 
                    type="time" 
                    value={formData.individuelle_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, individuelle_start: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ende</Label>
                  <Input 
                    type="time" 
                    value={formData.individuelle_ende}
                    onChange={(e) => setFormData(prev => ({ ...prev, individuelle_ende: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Wochentage</Label>
                <div className="flex gap-1 flex-wrap">
                  {WOCHENTAGE.map((tag, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleWochentag(idx)}
                      className={cn(
                        "px-2 py-1 text-xs rounded-md border transition-colors",
                        formData.individuelle_wochentage?.includes(idx)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background hover:bg-muted"
                      )}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Springer-Optionen */}
          {canEditAll && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4" />Springer-Konfiguration
                </Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.kann_besprungen_werden}
                      onChange={(e) => setFormData(prev => ({ ...prev, kann_besprungen_werden: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Kann besprungen werden</span>
                  </label>
                  <p className="text-xs text-muted-foreground ml-5">
                    Fahrzeug kann als Zusatzfunktion bei der Einteilung gewählt werden (z.B. DLK, RTW-X)
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input 
                      type="checkbox" 
                      checked={formData.ist_springer}
                      onChange={(e) => setFormData(prev => ({ ...prev, ist_springer: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Ist Springer-Fahrzeug</span>
                  </label>
                  <p className="text-xs text-muted-foreground ml-5">
                    Keine feste Besetzung - wird nur bei Bedarf aktiviert
                  </p>
                </div>
              </div>
            </>
          )}
          
          {/* Individuelle Funktionen (Custom-Funktionen pro Fahrzeug) */}
          {canEditAll && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />Einteilungsfunktionen
                  </Label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={formData.use_custom_funktionen}
                      onChange={(e) => setFormData(prev => ({ ...prev, use_custom_funktionen: e.target.checked }))}
                      className="rounded border-gray-300"
                      data-testid="checkbox-use-custom-funktionen"
                    />
                    <span>Individuell überschreiben</span>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">
                  {formData.use_custom_funktionen
                    ? 'Diese Funktionen erscheinen im Einteilungs-Dialog. Beispiele: "HYMO-MA", "ATF1", "TrF-DLK".'
                    : 'Es werden die Standard-Funktionen des Fahrzeugtyps verwendet.'}
                </p>

                {formData.use_custom_funktionen && (
                  <div className="space-y-2">
                    {(formData.custom_funktionen || []).map((fn, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`custom-funktion-row-${idx}`}>
                        <Input
                          placeholder="Kürzel z.B. HYMO-MA"
                          value={fn.kuerzel}
                          onChange={(e) => {
                            const arr = [...formData.custom_funktionen];
                            arr[idx] = { ...arr[idx], kuerzel: e.target.value };
                            setFormData(prev => ({ ...prev, custom_funktionen: arr }));
                          }}
                          className="w-32 font-mono text-sm"
                          data-testid={`custom-funktion-kuerzel-${idx}`}
                        />
                        <Input
                          placeholder="Bezeichnung z.B. Maschinist GW-H"
                          value={fn.name}
                          onChange={(e) => {
                            const arr = [...formData.custom_funktionen];
                            arr[idx] = { ...arr[idx], name: e.target.value };
                            setFormData(prev => ({ ...prev, custom_funktionen: arr }));
                          }}
                          className="flex-1 text-sm"
                          data-testid={`custom-funktion-name-${idx}`}
                        />
                        <label className="flex items-center gap-1 cursor-pointer text-xs whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={fn.ist_pflicht !== false}
                            onChange={(e) => {
                              const arr = [...formData.custom_funktionen];
                              arr[idx] = { ...arr[idx], ist_pflicht: e.target.checked };
                              setFormData(prev => ({ ...prev, custom_funktionen: arr }));
                            }}
                            data-testid={`custom-funktion-pflicht-${idx}`}
                          />
                          Pflicht
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const arr = formData.custom_funktionen.filter((_, i) => i !== idx);
                            setFormData(prev => ({ ...prev, custom_funktionen: arr }));
                          }}
                          data-testid={`custom-funktion-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          custom_funktionen: [...(prev.custom_funktionen || []), { kuerzel: '', name: '', ist_pflicht: true }],
                        }));
                      }}
                      data-testid="custom-funktion-add"
                    >
                      + Funktion hinzufügen
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Magnetboard-Farbe pro Fahrzeug */}
          {canEditAll && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-sm border" style={{ backgroundColor: formData.magnetboard_farbe || '#cbd5e1' }} />
                  Magnetboard-Farbe
                </Label>
                <p className="text-xs text-muted-foreground">
                  Farbe der Funktions-Zellen im Magnetboard und in der Schichtplanung.
                  Leer lassen → Default nach Fahrzeugtyp (LHF blau, RTW rot, DLK grün …).
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.magnetboard_farbe || '#1d4ed8'}
                    onChange={(e) => setFormData(prev => ({ ...prev, magnetboard_farbe: e.target.value }))}
                    className="w-12 h-10 rounded border cursor-pointer"
                    data-testid="magnetboard-farbe-picker"
                  />
                  <Input
                    type="text"
                    placeholder="#1d4ed8"
                    value={formData.magnetboard_farbe || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, magnetboard_farbe: e.target.value }))}
                    className="w-32 font-mono text-sm"
                    data-testid="magnetboard-farbe-input"
                  />
                  {formData.magnetboard_farbe && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, magnetboard_farbe: '' }))}
                      data-testid="magnetboard-farbe-reset"
                    >
                      Zurücksetzen
                    </Button>
                  )}
                </div>
                {/* Preset-Chips */}
                <div className="flex flex-wrap gap-1 pt-1">
                  {['#1d4ed8', '#dc2626', '#16a34a', '#eab308', '#7c3aed', '#0891b2', '#b45309', '#475569', '#0f172a'].map(hex => (
                    <button
                      key={hex}
                      type="button"
                      title={hex}
                      onClick={() => setFormData(prev => ({ ...prev, magnetboard_farbe: hex }))}
                      className={cn(
                        'w-7 h-7 rounded border-2 transition-transform hover:scale-110',
                        formData.magnetboard_farbe === hex ? 'border-foreground scale-110 shadow' : 'border-transparent'
                      )}
                      style={{ backgroundColor: hex }}
                      data-testid={`magnetboard-farbe-preset-${hex.slice(1)}`}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Sollstärke */}
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <Label>Sollstärke (Besatzung)</Label>
              {!canEditSollstaerke && (
                <Badge variant="outline" className="text-xs">Nur Ansicht</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Sun className="w-3.5 h-3.5" strokeWidth={1.5} />Tag
                </div>
                <Input 
                  type="number"
                  min={0}
                  max={10}
                  value={formData.staerke_tag}
                  onChange={(e) => setFormData(prev => ({ ...prev, staerke_tag: e.target.value }))}
                  disabled={!canEditSollstaerke}
                  className="text-center tabular-nums"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />Nacht
                </div>
                <Input 
                  type="number"
                  min={0}
                  max={10}
                  value={formData.staerke_nacht}
                  onChange={(e) => setFormData(prev => ({ ...prev, staerke_nacht: e.target.value }))}
                  disabled={!canEditSollstaerke}
                  className="text-center tabular-nums"
                />
              </div>
            </div>
            {!canEditSollstaerke && (
              <p className="text-xs text-muted-foreground">Nur Wachleitung kann die Sollstärke ändern.</p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// Gerät Hinzufügen/Bearbeiten Dialog
// =============================================

function GeraetDialog({ fahrzeug, geraet, open, onOpenChange, onSuccess }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', ablaufdatum: '' });
  
  const isEdit = !!geraet;
  
  useEffect(() => {
    if (open) {
      if (geraet) {
        // Bearbeiten-Modus
        setFormData({ 
          name: geraet.name || '', 
          ablaufdatum: geraet.ablaufdatum ? geraet.ablaufdatum.split('T')[0] : '' 
        });
      } else {
        // Neu-Modus
        setFormData({ name: '', ablaufdatum: '' });
      }
    }
  }, [open, geraet]);
  
  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Bitte Gerätename eingeben');
      return;
    }
    
    setIsLoading(true);
    try {
      let newGeraete;
      
      if (isEdit) {
        // Bestehendes Gerät aktualisieren
        newGeraete = (fahrzeug.geraete || []).map(g => 
          g.id === geraet.id 
            ? { ...g, name: formData.name, ablaufdatum: formData.ablaufdatum || null, aktualisiert_am: new Date().toISOString() }
            : g
        );
      } else {
        // Neues Gerät hinzufügen
        newGeraete = [
          ...(fahrzeug.geraete || []),
          { id: crypto.randomUUID(), name: formData.name, ablaufdatum: formData.ablaufdatum || null, erstellt_am: new Date().toISOString() }
        ];
      }
      
      await dienstApi.updateFahrzeug(fahrzeug.id, { geraete: newGeraete });
      toast.success(isEdit ? 'Gerät aktualisiert' : 'Gerät hinzugefügt');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Save device error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!geraet) return;
    
    setIsLoading(true);
    try {
      const newGeraete = (fahrzeug.geraete || []).filter(g => g.id !== geraet.id);
      await dienstApi.updateFahrzeug(fahrzeug.id, { geraete: newGeraete });
      toast.success('Gerät gelöscht');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Delete device error:', error);
      toast.error('Fehler beim Löschen');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleMarkErledigt = async () => {
    if (!geraet) return;
    
    // Setzt neues Ablaufdatum auf heute + 1 Jahr (Standard TÜV-Intervall)
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const newDate = nextYear.toISOString().split('T')[0];
    
    setIsLoading(true);
    try {
      const newGeraete = (fahrzeug.geraete || []).map(g => 
        g.id === geraet.id 
          ? { ...g, ablaufdatum: newDate, aktualisiert_am: new Date().toISOString() }
          : g
      );
      await dienstApi.updateFahrzeug(fahrzeug.id, { geraete: newGeraete });
      toast.success('TÜV erneuert - neues Ablaufdatum: ' + formatDate(newDate, { day: '2-digit', month: '2-digit', year: 'numeric' }));
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Update device error:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setIsLoading(false);
    }
  };
  
  const isExpired = geraet?.ablaufdatum && new Date(geraet.ablaufdatum) < new Date();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {isEdit ? 'Gerät bearbeiten' : 'Gerät hinzufügen'}
          </DialogTitle>
          {isEdit && isExpired && (
            <div className="flex items-center gap-2 p-2 mt-2 rounded-lg bg-red-50 border border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-700">TÜV abgelaufen!</span>
            </div>
          )}
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Gerätename *</Label>
            <Input 
              placeholder="z.B. Defibrillator" 
              value={formData.name} 
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} 
            />
          </div>
          <div className="space-y-2">
            <Label>TÜV-Ablaufdatum</Label>
            <Input 
              type="date" 
              value={formData.ablaufdatum} 
              onChange={(e) => setFormData(prev => ({ ...prev, ablaufdatum: e.target.value }))} 
            />
          </div>
          
          {/* Schnellaktion für abgelaufene TÜVs */}
          {isEdit && isExpired && (
            <Button 
              variant="secondary" 
              className="w-full gap-2 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200"
              onClick={handleMarkErledigt}
              disabled={isLoading}
            >
              <CheckCircle2 className="w-4 h-4" />
              TÜV erneuert (+ 1 Jahr)
            </Button>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isEdit && (
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={isLoading}
              className="sm:mr-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Löschen
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// Neues Fahrzeug Dialog
// =============================================

function NeuesFahrzeugDialog({ open, onOpenChange, onSuccess }) {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [fahrzeugtypen, setFahrzeugtypen] = useState([]);
  const [formData, setFormData] = useState({ 
    funkrufname: '', 
    kennzeichen: '', 
    fahrzeugtyp_id: '', 
    typ_detail: '', 
    bemerkung: '',
    staerke_tag: 2,
    staerke_nacht: 2,
    dienstzeit_modell: '24H',
    individuelle_start: '09:00',
    individuelle_ende: '21:00',
    individuelle_wochentage: [0,1,2,3,4,5,6],
    kann_besprungen_werden: false,
    ist_springer: false,
    tragen_typ: '',
    subtyp: ''
  });
  const [selectedTypCode, setSelectedTypCode] = useState('');
  
  const canEditSollstaerke = ['DEVELOPER', 'WACHLEITUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'].includes(user?.rolle);
  const isRTW = selectedTypCode?.includes('RTW');
  
  useEffect(() => {
    if (open) {
      setFormData({ 
        funkrufname: '', 
        kennzeichen: '', 
        fahrzeugtyp_id: '', 
        typ_detail: '', 
        bemerkung: '',
        staerke_tag: 2,
        staerke_nacht: 2,
        dienstzeit_modell: '24H',
        individuelle_start: '09:00',
        individuelle_ende: '21:00',
        individuelle_wochentage: [0,1,2,3,4,5,6],
        kann_besprungen_werden: false,
        ist_springer: false,
        tragen_typ: '',
        subtyp: ''
      });
      setSelectedTypCode('');
      loadFahrzeugtypen();
    }
  }, [open]);
  
  const loadFahrzeugtypen = async () => {
    try {
      const res = await dienstApi.getFahrzeugtypen();
      setFahrzeugtypen(res.data || []);
    } catch (error) {
      console.error('Load types error:', error);
    }
  };
  
  const handleTypChange = (typId) => {
    setFormData(prev => ({ ...prev, fahrzeugtyp_id: typId }));
    const typ = fahrzeugtypen.find(t => t.id === typId);
    setSelectedTypCode(typ?.typ_code || '');
    // Automatisch kann_besprungen_werden vom Typ übernehmen
    if (typ?.kann_besprungen_werden !== undefined) {
      setFormData(prev => ({ ...prev, kann_besprungen_werden: typ.kann_besprungen_werden }));
    }
    // Standard-Besatzung vom Typ übernehmen
    if (typ?.standard_besatzung !== undefined) {
      setFormData(prev => ({ 
        ...prev, 
        staerke_tag: typ.standard_besatzung,
        staerke_nacht: typ.standard_besatzung 
      }));
    }
  };
  
  const toggleWochentag = (tag) => {
    setFormData(prev => {
      const wt = prev.individuelle_wochentage || [];
      if (wt.includes(tag)) {
        return { ...prev, individuelle_wochentage: wt.filter(t => t !== tag) };
      } else {
        return { ...prev, individuelle_wochentage: [...wt, tag].sort() };
      }
    });
  };
  
  const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const verfuegbareTypen = FAHRZEUG_TYPEN_DETAIL[selectedTypCode] || [];
  
  const handleSubmit = async () => {
    if (!formData.funkrufname || !formData.fahrzeugtyp_id) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setIsLoading(true);
    try {
      const tagValue = parseInt(formData.staerke_tag);
      const nachtValue = parseInt(formData.staerke_nacht);
      const createData = {
        funkrufname: formData.funkrufname,
        kennzeichen: formData.kennzeichen || null,
        fahrzeugtyp_id: formData.fahrzeugtyp_id,
        typ_detail: formData.typ_detail || null,
        bemerkung: formData.bemerkung || null,
        staerke_tag: isNaN(tagValue) ? 2 : tagValue,
        staerke_nacht: isNaN(nachtValue) ? 2 : nachtValue,
        dienstzeit_modell: formData.dienstzeit_modell,
        kann_besprungen_werden: formData.kann_besprungen_werden,
        ist_springer: formData.ist_springer
      };
      
      // Individuelle Dienstzeit
      if (formData.dienstzeit_modell === 'SONDER') {
        createData.individuelle_dienstzeit = {
          start_zeit: formData.individuelle_start,
          end_zeit: formData.individuelle_ende,
          wochentage: formData.individuelle_wochentage
        };
      }
      
      // Tragen-Typ für RTW
      if (isRTW && formData.tragen_typ) {
        createData.tragen_typ = formData.tragen_typ;
      }
      
      await dienstApi.createFahrzeug(createData);
      toast.success('Fahrzeug erstellt');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Create error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler beim Erstellen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" />Neues Fahrzeug</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Funkrufname *</Label>
            <Input placeholder="z.B. LHF 4600/2" value={formData.funkrufname} onChange={(e) => setFormData(prev => ({ ...prev, funkrufname: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Kennzeichen</Label>
            <Input placeholder="z.B. B-FW 4600" value={formData.kennzeichen} onChange={(e) => setFormData(prev => ({ ...prev, kennzeichen: e.target.value }))} className="font-mono" />
          </div>
          <div className="space-y-2">
            <Label>Fahrzeugtyp *</Label>
            <Select value={formData.fahrzeugtyp_id} onValueChange={handleTypChange}>
              <SelectTrigger><SelectValue placeholder="Typ auswählen..." /></SelectTrigger>
              <SelectContent>
                {fahrzeugtypen.map(typ => (
                  <SelectItem key={typ.id} value={typ.id}>
                    {typ.typ_code} - {typ.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {verfuegbareTypen.length > 0 && (
            <div className="space-y-2">
              <Label>Typ-Variante</Label>
              <Select value={formData.typ_detail} onValueChange={(v) => setFormData(prev => ({ ...prev, typ_detail: v }))}>
                <SelectTrigger><SelectValue placeholder="Variante wählen..." /></SelectTrigger>
                <SelectContent>
                  {verfuegbareTypen.map(typ => (
                    <SelectItem key={typ} value={typ}>{typ}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* RTW Tragen-Typ & Subtyp */}
          {isRTW && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tragen-Typ</Label>
                <Select value={formData.tragen_typ || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, tragen_typ: v }))}>
                  <SelectTrigger><SelectValue placeholder="Trage wählen..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STOLLENWERK">Stollenwerk</SelectItem>
                    <SelectItem value="STRYKER">Stryker Power-PRO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>RTW-Variante</Label>
                <Select value={formData.subtyp || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, subtyp: v }))}>
                  <SelectTrigger><SelectValue placeholder="B oder C..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B">RTW-B</SelectItem>
                    <SelectItem value="C">RTW-C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Bemerkung</Label>
            <Textarea placeholder="Optionale Bemerkung..." value={formData.bemerkung} onChange={(e) => setFormData(prev => ({ ...prev, bemerkung: e.target.value }))} rows={2} />
          </div>
          
          <Separator />
          
          {/* Dienstzeit-Modell */}
          <div className="space-y-2">
            <Label>Dienstzeit-Modell</Label>
            <Select value={formData.dienstzeit_modell} onValueChange={(v) => setFormData(prev => ({ ...prev, dienstzeit_modell: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24H">24-Stunden (07:00 - 07:00)</SelectItem>
                <SelectItem value="TAG">Nur Tagdienst (07:00 - 19:00)</SelectItem>
                <SelectItem value="NACHT">Nur Nachtdienst (19:00 - 07:00)</SelectItem>
                <SelectItem value="SONDER">Individuell</SelectItem>
                <SelectItem value="SPRINGER">Springer (keine feste Zeit)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Individuelle Dienstzeit */}
          {formData.dienstzeit_modell === 'SONDER' && (
            <div className="space-y-3 p-3 rounded-lg bg-muted/50">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Start</Label>
                  <Input 
                    type="time" 
                    value={formData.individuelle_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, individuelle_start: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ende</Label>
                  <Input 
                    type="time" 
                    value={formData.individuelle_ende}
                    onChange={(e) => setFormData(prev => ({ ...prev, individuelle_ende: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Wochentage</Label>
                <div className="flex gap-1 flex-wrap">
                  {WOCHENTAGE.map((tag, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleWochentag(idx)}
                      className={cn(
                        "px-2 py-1 text-xs rounded-md border transition-colors",
                        formData.individuelle_wochentage?.includes(idx)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background hover:bg-muted"
                      )}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Springer-Optionen */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4" />Springer-Konfiguration
            </Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.kann_besprungen_werden}
                  onChange={(e) => setFormData(prev => ({ ...prev, kann_besprungen_werden: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Kann besprungen werden</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.ist_springer}
                  onChange={(e) => setFormData(prev => ({ ...prev, ist_springer: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Ist Springer-Fahrzeug (keine feste Besetzung)</span>
              </label>
            </div>
          </div>
          
          {/* Sollstärke */}
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <Label>Sollstärke (Besatzung)</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Sun className="w-3 h-3 text-amber-500" />Tagdienst
                </div>
                <Input 
                  type="number"
                  min={0}
                  max={10}
                  value={formData.staerke_tag}
                  onChange={(e) => setFormData(prev => ({ ...prev, staerke_tag: e.target.value }))}
                  className="text-center"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Moon className="w-3 h-3 text-indigo-500" />Nachtdienst
                </div>
                <Input 
                  type="number"
                  min={0}
                  max={10}
                  value={formData.staerke_nacht}
                  onChange={(e) => setFormData(prev => ({ ...prev, staerke_nacht: e.target.value }))}
                  className="text-center"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>{isLoading ? 'Erstellen...' : 'Fahrzeug erstellen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// Haupt-Seite
// =============================================

export default function FahrzeugePage() {
  const { user, hasEffectivePermission } = useAuthStore();
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFahrzeug, setSelectedFahrzeug] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [neuesFahrzeugOpen, setNeuesFahrzeugOpen] = useState(false);
  useFahrzeugBilder(); // reaktive Custom-Bilder
  
  const canEdit = hasEffectivePermission('fahrzeuge.manage');
  
  // Check for selected vehicle from URL (for Dashboard linking)
  const searchParams = new URLSearchParams(window.location.search);
  const selectedIdFromUrl = searchParams.get('selected');
  
  const loadFahrzeuge = async () => {
    setIsLoading(true);
    try {
      const res = await dienstApi.getFahrzeuge();
      const sorted = (res.data || []).sort((a, b) => {
        const typA = a.fahrzeugtyp_code || '';
        const typB = b.fahrzeugtyp_code || '';
        if (typA !== typB) return typA.localeCompare(typB);
        return (a.funkrufname || '').localeCompare(b.funkrufname || '');
      });
      setFahrzeuge(sorted);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fahrzeuge konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => { loadFahrzeuge(); }, []);
  
  // Auto-open modal if selected vehicle ID is in URL
  useEffect(() => {
    if (selectedIdFromUrl && fahrzeuge.length > 0 && !detailsOpen) {
      const fz = fahrzeuge.find(f => f.id === selectedIdFromUrl);
      if (fz) {
        setSelectedFahrzeug(fz);
        setDetailsOpen(true);
        // Clear URL parameter after opening
        window.history.replaceState({}, '', '/fahrzeuge');
      }
    }
  }, [selectedIdFromUrl, fahrzeuge, detailsOpen]);
  
  const handleKarteClick = (fahrzeug) => {
    setSelectedFahrzeug(fahrzeug);
    setDetailsOpen(true);
  };
  
  // Refresh selected vehicle after update
  const refreshSelectedFahrzeug = async (fahrzeugId) => {
    try {
      const res = await dienstApi.getFahrzeuge();
      const allFahrzeuge = res.data || [];
      setFahrzeuge(allFahrzeuge.sort((a, b) => {
        const typA = a.fahrzeugtyp_code || '';
        const typB = b.fahrzeugtyp_code || '';
        if (typA !== typB) return typA.localeCompare(typB);
        return (a.funkrufname || '').localeCompare(b.funkrufname || '');
      }));
      // Update selected vehicle with fresh data
      const updated = allFahrzeuge.find(f => f.id === fahrzeugId);
      if (updated) {
        setSelectedFahrzeug(updated);
      }
    } catch (e) {
      console.error('Refresh error:', e);
    }
  };
  
  const handleUpdate = async () => {
    if (selectedFahrzeug) {
      await refreshSelectedFahrzeug(selectedFahrzeug.id);
    } else {
      await loadFahrzeuge();
    }
  };
  
  const aktiveFahrzeuge = fahrzeuge.filter(f => f.status !== 'AUSSERDIENST' && !f.ist_springer);
  const springerFahrzeuge = fahrzeuge.filter(f => f.ist_springer);
  
  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Fahrzeuge</h1>
            <p className="text-muted-foreground">Fahrzeugflotte der Wache verwalten</p>
          </div>
          {canEdit && (
            <Button onClick={() => setNeuesFahrzeugOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />Neues Fahrzeug
            </Button>
          )}
        </div>
        
        {/* Hauptinhalt */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i}><Skeleton className="h-44" /><CardContent className="p-4"><Skeleton className="h-6 w-3/4 mb-2" /><Skeleton className="h-4 w-1/2" /></CardContent></Card>
            ))}
          </div>
        ) : fahrzeuge.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">Keine Fahrzeuge vorhanden</p>
              <p className="text-muted-foreground mb-4">Fügen Sie Fahrzeuge zur Wache hinzu.</p>
              {canEdit && <Button onClick={() => setNeuesFahrzeugOpen(true)}><Plus className="w-4 h-4 mr-2" />Erstes Fahrzeug anlegen</Button>}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {aktiveFahrzeuge.map(fahrzeug => (
                <FahrzeugKarte key={fahrzeug.id} fahrzeug={fahrzeug} onClick={handleKarteClick} />
              ))}
            </div>
            {springerFahrzeuge.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2"><Wrench className="w-5 h-5" />Springer / Reserve</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {springerFahrzeuge.map(fahrzeug => (
                    <FahrzeugKarte key={fahrzeug.id} fahrzeug={fahrzeug} onClick={handleKarteClick} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Details Modal */}
      <FahrzeugDetailsModal 
        fahrzeug={selectedFahrzeug}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        onUpdate={handleUpdate}
      />
      
      {/* Neues Fahrzeug Dialog */}
      <NeuesFahrzeugDialog open={neuesFahrzeugOpen} onOpenChange={setNeuesFahrzeugOpen} onSuccess={loadFahrzeuge} />
    </MainLayout>
  );
}
